"use client";
import { useMemo, useRef, useState } from "react";
import usePublicContacts, { phoneTel, phoneDisplay } from '@/hooks/usePublicContacts';
import Link from "next/link";
import Header from "@/components/landing/Header";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "@/components/landing/RichText";
import Footer from "@/components/landing/Footer";
import CTASection from "@/components/landing/CTASection";
import FAQSection, { type FAQCategory, type FAQItem } from "@/components/landing/FAQSection";
import {
  Search, HelpCircle, BookOpen, CreditCard, MessageSquare, ShieldCheck, Bus,
  ArrowRight, ArrowLeft, Phone, Mail, X, FileText, Layers,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
   Topics — the six cards. Each one filters the index below; the
   count on a card is the real number of indexed entries for it,
   not a hand-written number.
   ───────────────────────────────────────────────────────────── */
type Topic = {
  id: string;
  icon: LucideIcon;
  title: string;
  desc: string;
  color: string;
};

/* Icon, colour and the topic id used by the resource filter. Titles and
   descriptions come from `support.topics`, index-aligned with this list. */
const TOPIC_META = [
  { id: "setup", icon: BookOpen, color: "#3D5EE1" },
  { id: "fees", icon: CreditCard, color: "#65A30D" },
  { id: "notifications", icon: MessageSquare, color: "#D97706" },
  { id: "reports", icon: HelpCircle, color: "#0284C7" },
  { id: "transport", icon: Bus, color: "#BE123C" },
  { id: "security", icon: ShieldCheck, color: "#4338CA" },
];

type SupportFAQItem = FAQItem & { topics: string[]; keywords?: string[] };

/* The topic tags and search keywords for each FAQ. The question and answer
   themselves come from `support.faqItems`, index-aligned with this list. */
const FAQ_META = [
  { topics: ["setup"], keywords: ["onboarding", "setup time", "go live", "implementation", "migration", "training"] },
  { topics: ["notifications"], keywords: ["sms", "dlt", "email", "smtp", "push", "app notification", "parent chat", "whatsapp"] },
  { topics: ["fees"], keywords: ["upi", "net banking", "razorpay", "payu", "phonepe", "paytm", "cashfree", "stripe", "paypal", "online payment", "receipt"] },
  { topics: ["reports"], keywords: ["cbse", "icse", "marksheet", "grading", "report card", "pass rule", "affiliation", "seal"] },
  { topics: ["transport"], keywords: ["gps", "bus", "live tracking", "route", "rfid", "gate", "pickup", "drop"] },
  { topics: ["security"], keywords: ["encryption", "https", "isolated", "backup", "audit log", "otp", "permissions", "compliance"] },
];

/* ─────────────────────────────────────────────────────────────
   The searchable index.

   Two kinds of entry, and nothing else: an ANSWER, which is read
   in place, and a LINK to a page that genuinely exists. There is
   deliberately no "guide" kind any more — /user-guides was a list
   of guide titles that opened nothing, so search results used to
   promise a document and deliver a dead end. Anything it claimed
   to cover is answered here instead.

   Every claim below is drawn from copy already on the site (the
   FAQ on this page and the site-wide set in FAQSection), so the
   support page can't drift away from what we tell people
   elsewhere.
   ───────────────────────────────────────────────────────────── */
type ResourceKind = "answer" | "article" | "page";

type ResourceMeta = Omit<Resource, "title" | "desc">;

type Resource = {
  id: string;
  kind: ResourceKind;
  title: string;
  /** For an answer this IS the answer — it is rendered in full. */
  desc: string;
  /** Answers may add one; articles and pages always have one. */
  href?: string;
  /** Only widens the search haystack — no longer drives any UI. */
  topics: string[];
  /** Search terms that do not appear in the title or description. */
  keywords?: string[];
};

/* The badge label and the card CTA come from `support.kinds`; only the icon
   and the accent colour live here. */
const KIND_META: Record<ResourceKind, { icon: LucideIcon; color: string }> = {
  answer: { icon: HelpCircle, color: "#3D5EE1" },
  article: { icon: FileText, color: "#0284C7" },
  page: { icon: Layers, color: "#65A30D" },
};

/* Structure only: which topic a resource belongs to, where it links, and
   the English keywords it can be found by. Titles and descriptions come
   from `support.resources`, keyed by the same id. */
const LINKED_RESOURCES: ResourceMeta[] = [
  /* ── Getting started ── */
  {
    id: "migration",
    kind: "answer",
    href: "/contact",
    topics: ["setup"],
    keywords: ["student import", "bulk upload", "excel", "csv", "migration", "data transfer", "legacy", "spreadsheet"],
  },
  {
    id: "staff-roles",
    kind: "answer",
    topics: ["setup", "security"],
    keywords: ["rbac", "access control", "designation", "module permissions", "teacher login", "otp", "multi branch"],
  },

  /* ── Fees ── */
  {
    id: "fee-structures",
    kind: "answer",
    topics: ["fees"],
    keywords: ["fee head", "concession", "discount", "installment", "due date", "fee setup", "term fee", "structure"],
  },
  {
    id: "receipts",
    kind: "answer",
    topics: ["fees"],
    keywords: ["receipt", "ledger", "outstanding", "defaulter", "pending fees", "reconciliation", "accounting", "export", "payment gateway"],
  },
  {
    id: "fee-article",
    kind: "article",
    href: "/blog/fee-collection-always-almost-on-target",
    topics: ["fees"],
    keywords: ["collection", "follow up", "reminders", "school finance"],
  },

  /* ── Notifications ── */
  {
    id: "broadcasts",
    kind: "answer",
    topics: ["notifications"],
    keywords: ["sms alerts", "dlt", "smtp", "push notification", "circular", "announcement", "broadcast", "reminder", "chat", "whatsapp", "notice"],
  },
  {
    id: "whatsapp-article",
    kind: "article",
    href: "/blog/whatsapp-groups-hidden-cost",
    topics: ["notifications"],
    keywords: ["whatsapp", "groups", "school operations", "communication"],
  },

  /* ── Exams & report cards ── */
  {
    id: "marks-entry",
    kind: "answer",
    topics: ["reports"],
    keywords: ["exam", "marks entry", "grade", "term", "result", "pass rule", "theory", "practical", "grading scale", "marksheet"],
  },

  /* ── Attendance — no category card, but people search for it ── */
  {
    id: "attendance",
    kind: "answer",
    topics: [],
    keywords: ["attendance", "biometric", "face", "geofence", "punch", "register", "absent", "present", "report", "rfid"],
  },

  /* ── Transport ── */
  {
    id: "routes",
    kind: "answer",
    topics: ["transport"],
    keywords: ["route", "stop", "driver", "vehicle", "fleet", "transport setup", "bus", "transport fee", "gps", "tracking"],
  },

  /* ── Security ── */
  {
    id: "audit-log",
    kind: "answer",
    topics: ["security"],
    keywords: ["audit", "activity log", "compliance", "history", "tracking changes", "who changed"],
  },
  {
    id: "data-ownership",
    kind: "answer",
    topics: ["security"],
    keywords: ["data ownership", "export", "excel", "pdf", "backup", "download", "portability", "leave"],
  },
  /* The `status` entry (→ /system-status) is parked while that page is
     archived for showing hand-typed uptime numbers — restore it here when
     the page relaunches with real monitoring data. */

  /* ── Reachable from search only ── */
  {
    id: "features",
    kind: "page",
    href: "/features",
    topics: [],
    keywords: ["features", "modules", "catalogue", "what is included", "library", "hostel", "payroll", "timetable", "admissions"],
  },
  {
    id: "plans",
    kind: "page",
    href: "/plans",
    topics: [],
    keywords: ["price", "pricing", "cost", "plan", "subscription", "renewal", "invoice", "billing", "quote", "trial"],
  },
  {
    id: "buying-article",
    kind: "article",
    href: "/blog/choosing-school-erp-tier2-tier3-checklist",
    topics: [],
    keywords: ["buying guide", "choosing", "vendor", "comparison", "evaluate", "checklist", "switch"],
  },
  {
    id: "contact",
    kind: "page",
    href: "/contact",
    topics: [],
    keywords: ["contact", "demo", "call", "support team", "help", "ticket", "raise issue", "talk", "tour", "overview", "training"],
  },
];

/* ─────────────────────────────────────────────────────────────
   Matching. Terms are OR-matched and ranked by how many of them
   an entry hits — a two-word query like "Fee Setup" should still
   surface fee entries that never use the word "setup".
   ───────────────────────────────────────────────────────────── */
const STOPWORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "by", "can", "do", "does", "for", "from",
  "how", "in", "is", "it", "my", "of", "on", "or", "our", "the", "to", "we", "what",
  "when", "where", "which", "with", "you", "your",
]);

/* Devanagari is in the allowed set alongside a-z0-9. Stripping to ASCII
   would reduce every Hindi title and query to an empty string, so search
   would silently return nothing for every Hindi term — worse than not
   translating the page at all. */
const normalize = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9\u0900-\u097F]+/g, " ").trim();

const queryTerms = (q: string) =>
  normalize(q).split(" ").filter((t) => t.length > 1 && !STOPWORDS.has(t));

/* Crude de-pluralisation so "alerts" finds "alert" and "fees" finds "fee". */
const hits = (haystack: string, term: string) =>
  haystack.includes(term) || (term.length > 3 && term.endsWith("s") && haystack.includes(term.slice(0, -1)));

type IndexedResource = Resource & { hayTitle: string; hayKeywords: string; hayDesc: string };

/* Built per language rather than once at module scope: the haystacks are
   derived from the TRANSLATED title and description, so an index frozen at
   import time would keep answering English queries after a switch to Hindi. */
const buildIndex = (resources: Resource[], topicTitles: Record<string, string>): IndexedResource[] =>
  resources.map((r) => {
  const topicNames = r.topics.map((id) => topicTitles[id] ?? "").join(" ");
  return {
    ...r,
    hayTitle: normalize(r.title),
    /* Keywords stay English on purpose — indexing them next to the
       translated title and desc means either language finds the entry. */
    hayKeywords: normalize([...(r.keywords ?? []), topicNames, r.kind].join(" ")),
    hayDesc: normalize(r.desc),
  };
});

const searchResources = (searchIndex: IndexedResource[], query: string): Resource[] => {
  const terms = queryTerms(query);
  if (!terms.length) return [];

  const scored = searchIndex
    .map((entry) => {
      let matched = 0;
      let score = 0;
      for (const term of terms) {
        let termScore = 0;
        if (hits(entry.hayTitle, term)) termScore += 4;
        if (hits(entry.hayKeywords, term)) termScore += 3;
        if (hits(entry.hayDesc, term)) termScore += 1;
        if (termScore > 0) {
          matched += 1;
          score += termScore;
        }
      }
      /* Covering more of the query outranks matching one term loudly. */
      return { entry, matched, score: matched * 100 + score };
    })
    .filter((r) => r.matched > 0)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return [];

  /* Precision over recall: entries covering the most query terms win the
     list outright. Without this, a two-word query like "Fee Setup" drags
     in every entry that merely says "setup" somewhere — 15 of 21 results,
     which is no more useful than no search at all. When the winning tier
     is very short (a long natural-language question rarely matches in
     full), widen by one tier so there is still something to read. */
  const best = scored[0].matched;
  const kept = scored.filter((r) => r.matched === best);
  if (kept.length < 3 && best > 1) {
    kept.push(...scored.filter((r) => r.matched === best - 1).slice(0, 4));
  }

  return kept.map((r) => r.entry);
};

/* Popular search chips. Translated, because clicking one runs it as a query
   and a Hindi reader should get a Hindi query against Hindi copy. */

/* Destinations and icons; labels come from `support.directLinks`, matched
   BY INDEX — a row added or removed here must make the same change to the
   `directLinks` array in en.ts and hi.ts or every later label shifts.
   (/system-status is parked while that page is archived.) */
const DIRECT_LINKS: { href: string; icon: LucideIcon }[] = [
  { href: "/features", icon: Layers },
  { href: "/blog", icon: FileText },
  { href: "/plans", icon: CreditCard },
  { href: "/contact", icon: MessageSquare },
];

/* ── A single search result ── */
function ResourceCard({ resource }: { resource: Resource }) {
  const { t } = useLanguage();
  const meta = KIND_META[resource.kind];
  const Icon = meta.icon;
  const kindLabel = t(`support.kinds.${resource.kind}.label`);
  const kindCta = t(`support.kinds.${resource.kind}.cta`);

  const body = (
    <div className="flex items-start gap-2.5 sm:gap-4">
      <div
        className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
      >
        <Icon size={16} className="sm:hidden" />
        <Icon size={19} className="hidden sm:block" />
      </div>

      <div className="min-w-0 flex-1">
        <span
          className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide"
          style={{ color: meta.color }}
        >
          {kindLabel}
        </span>
        <h3 className="text-xs sm:text-[15px] font-bold text-slate-800 leading-snug mt-0.5 mb-1 group-hover:text-[#3D5EE1] transition-colors">
          {resource.title}
        </h3>
        <p className="text-[10.5px] sm:text-[13px] text-slate-500 leading-relaxed font-medium">
          {resource.desc}
        </p>
        {resource.href ? (
          <span className="mt-2 inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold text-[#3D5EE1] group-hover:translate-x-0.5 transition-transform">
            {kindCta}
            <ArrowRight size={12} />
          </span>
        ) : null}
      </div>
    </div>
  );

  const shell =
    "bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-slate-200/80 shadow-xs transition-all duration-300 block";

  /* FAQ answers carry their own text, so they are read in place rather
     than linked anywhere — a link would only scroll past the answer. */
  return resource.href ? (
    <Link
      href={resource.href}
      className={`${shell} group hover:shadow-md hover:border-[#3D5EE1]/40 hover:-translate-y-0.5`}
    >
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  );
}

export default function SupportContent() {
  const contacts = usePublicContacts();
  const { t, tv, tf } = useLanguage();

  /* Everything readable is resolved here, so a language switch rebuilds the
     topic cards, the resource copy AND the search index together. Memoised on
     `tv`, whose identity changes only when the language does. */
  const topics = useMemo(
    () => tv<{ title: string; desc: string }[]>("support.topics")
      .map((copy, i) => ({ ...copy, ...TOPIC_META[i] })),
    [tv]
  );

  const faqItems = useMemo(
    () => tv<{ question: string; answer: string }[]>("support.faqItems")
      .map((copy, i) => ({ ...copy, ...FAQ_META[i] })),
    [tv]
  );

  const supportFaq: FAQCategory[] = useMemo(
    () => [{
      id: "common",
      label: t("support.faqLabel"),
      icon: HelpCircle,
      items: faqItems.map(({ question, answer }) => ({ question, answer })),
    }],
    [t, faqItems]
  );

  const resources: Resource[] = useMemo(() => {
    const copy = tv<Record<string, { title: string; desc: string }>>("support.resources");
    return [
      ...LINKED_RESOURCES.map((r) => ({
        ...r,
        title: copy?.[r.id]?.title ?? r.id,
        desc: copy?.[r.id]?.desc ?? "",
      })),
      ...faqItems.map((f, i) => ({
        id: `faq-${i}`,
        kind: "answer" as const,
        title: f.question,
        desc: f.answer,
        topics: f.topics,
        keywords: f.keywords,
      })),
    ];
  }, [tv, faqItems]);

  const searchIndex = useMemo(
    () => buildIndex(resources, Object.fromEntries(topics.map((x) => [x.id, x.title]))),
    [resources, topics]
  );

  const popular = tv<string[]>("support.popular");
  const directLinkLabels = tv<string[]>("support.directLinks");

  const [searchQuery, setSearchQuery] = useState("");
  const resultsRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const results = useMemo(
    () => (trimmedQuery ? searchResources(searchIndex, trimmedQuery) : []),
    [trimmedQuery],
  );

  const scrollToResults = () => {
    /* rAF so the results section exists before we scroll to it. */
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const runSearch = (value: string) => {
    setSearchQuery(value);
    scrollToResults();
  };

  const clearSearch = ({ focus = false } = {}) => {
    setSearchQuery("");
    if (focus) inputRef.current?.focus();
  };

  return (
    <div className="landing-root min-h-screen bg-[#FAFCFF] font-sans">
      <Header />

      <main className="pt-[76px]">
        {/* ── Hero Search Banner ── */}
        <section className="relative w-full bg-gradient-to-b from-[#EBF7F4] via-[#F4FAF8] to-[#FAFCFF] py-10 sm:py-24 px-3 sm:px-4 overflow-hidden border-b border-brand-100/60">
          <div className="max-w-[1200px] mx-auto text-center relative z-10 flex flex-col items-center">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 py-1 px-3 sm:py-1.5 sm:px-4 rounded-full bg-white border border-brand-100 shadow-xs mb-3 sm:mb-4">
              <HelpCircle size={13} className="text-[#2A48C9]" />
              <span className="text-[10px] sm:text-xs font-bold text-[#2A48C9] tracking-wide uppercase">{t("support.badge")}</span>
            </div>

            <h1 className="text-2xl sm:text-5xl font-light text-slate-900 tracking-tight mb-2 sm:mb-4">
              <RichText text={t("support.heading")} accentClassName="font-semibold text-[#3D5EE1]" />
            </h1>
            <p className="text-xs sm:text-base text-slate-600 max-w-[560px] mb-5 sm:mb-8 font-medium leading-relaxed px-2">
              {t("support.subtitle")}
            </p>

            {/* Search Input Box */}
            <form
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                if (!trimmedQuery) return;
                inputRef.current?.blur();
                scrollToResults();
              }}
              className="w-full max-w-[620px] relative"
            >
              <label htmlFor="support-search" className="sr-only">
                {t("support.searchLabel")}
              </label>
              <Search size={16} className="sm:hidden absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Search size={20} className="hidden sm:block absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                id="support-search"
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder={t("support.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") clearSearch();
                }}
                aria-describedby="support-search-status"
                className="w-full pl-10 sm:pl-14 pr-11 sm:pr-14 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-white border border-brand-200/80 text-xs sm:text-sm font-medium text-slate-800 placeholder-slate-400 shadow-md sm:shadow-lg focus:outline-none focus:border-[#3D5EE1] focus:ring-2 focus:ring-[#3D5EE1]/15 transition-all [&::-webkit-search-cancel-button]:hidden"
              />
              {trimmedQuery ? (
                <button
                  type="button"
                  onClick={() => clearSearch({ focus: true })}
                  aria-label={t("support.clearSearch")}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <X size={14} className="sm:w-4 sm:h-4" />
                </button>
              ) : null}
            </form>

            {/* Quick Keyword Pills */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 text-[11px] sm:text-xs text-slate-500 font-medium">
              <span className="text-slate-400">Popular:</span>
              {popular.map((tag) => {
                const isActive = trimmedQuery === tag;
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => (isActive ? clearSearch() : runSearch(tag))}
                    className={`px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full border transition-colors shadow-2xs text-[10.5px] sm:text-xs cursor-pointer ${
                      isActive
                        ? "bg-[#3D5EE1] border-[#3D5EE1] text-white"
                        : "bg-white/80 border-slate-200 hover:border-[#3D5EE1] hover:text-[#3D5EE1]"
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            {/* Announced to screen readers on every query change */}
            <p id="support-search-status" role="status" aria-live="polite" className="sr-only">
              {isSearching
                ? tf(results.length === 1 ? "support.srResultOne" : "support.srResultOther", { count: results.length, query: trimmedQuery })
                : ""}
            </p>
          </div>
        </section>

        {/* ── Search results, or the category browser ── */}
        <section
          ref={resultsRef}
          id="support-results"
          className="max-w-[1280px] mx-auto px-3 sm:px-6 py-10 sm:py-16 scroll-mt-20"
        >
          {!isSearching ? (
            <>
              <div className="text-center mb-6 sm:mb-12">
                <h2 className="text-xl sm:text-3xl font-light text-slate-900">
                  <RichText text={t("support.browseHeading")} accentClassName="font-semibold text-[#3D5EE1]" />
                </h2>
                <p className="mt-2 text-[11px] sm:text-sm text-slate-500 font-medium">
                  {t("support.browseSubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-6">
                {topics.map((topic) => {
                  const Icon = topic.icon;
                  return (
                    /* Every card points at the blog index for now. Once posts
                       exist per topic, this becomes /blog?category=<topic>. */
                    <Link
                      key={topic.id}
                      href="/blog"
                      className="text-left bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-slate-200/80 shadow-xs hover:shadow-md hover:border-[#3D5EE1]/40 hover:-translate-y-0.5 transition-all duration-300 group flex flex-col justify-between cursor-pointer"
                    >
                      <div>
                        <div className="flex items-start sm:items-center justify-between gap-1.5 mb-2 sm:mb-4">
                          <div
                            className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${topic.color}15`, color: topic.color }}
                          >
                            <Icon size={16} className="sm:hidden" />
                            <Icon size={22} className="hidden sm:block" />
                          </div>
                        </div>

                        <h3 className="text-xs sm:text-lg font-bold text-slate-800 mb-1 sm:mb-2 group-hover:text-[#3D5EE1] transition-colors leading-snug">
                          {topic.title}
                        </h3>
                        <p className="text-[10.5px] sm:text-sm text-slate-500 leading-relaxed font-medium mb-2 sm:mb-6 line-clamp-2 sm:line-clamp-none">
                          {topic.desc}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 text-[10px] sm:text-xs font-bold text-[#3D5EE1] group-hover:translate-x-1 transition-transform mt-auto">
                        <span>{t("support.exploreGuides")}</span>
                        <ArrowRight size={11} className="sm:hidden" />
                        <ArrowRight size={14} className="hidden sm:block" />
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Straight links out, for readers who already know where they're going */}
              <div className="mt-6 sm:mt-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                {DIRECT_LINKS.map(({ href, icon: Icon }, i) => (
                  <Link
                    key={href}
                    href={href}
                    className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 sm:px-4 sm:py-2 text-[10.5px] sm:text-xs font-bold text-slate-600 shadow-2xs hover:border-[#3D5EE1] hover:text-[#3D5EE1] transition-colors"
                  >
                    <Icon size={13} className="sm:w-[15px] sm:h-[15px]" />
                    {directLinkLabels?.[i] ?? href}
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* ── Results header ── */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5 sm:mb-8">
                <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0">
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-2xl font-bold text-slate-900 leading-tight truncate">
                      Results for &ldquo;{trimmedQuery}&rdquo;
                    </h2>
                    <p className="text-[11px] sm:text-sm text-slate-500 font-medium">
                      {tf(results.length === 1 ? "support.resultsCountOne" : "support.resultsCountOther", { count: results.length })}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => clearSearch()}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 sm:px-4 sm:py-2 text-[10.5px] sm:text-xs font-bold text-slate-600 shadow-2xs hover:border-[#3D5EE1] hover:text-[#3D5EE1] transition-colors cursor-pointer shrink-0"
                >
                  <ArrowLeft size={13} />
                  {t("support.allCategories")}
                </button>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 sm:gap-4">
                  {results.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </div>
              ) : (
                /* ── Nothing matched: hand the reader straight to a human ── */
                <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs px-5 py-10 sm:px-8 sm:py-14 text-center">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Search size={20} className="sm:w-6 sm:h-6" />
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold text-slate-800">
                    {tf("support.noResults", { query: trimmedQuery })}
                  </h3>
                  <p className="mt-1.5 text-[11px] sm:text-sm text-slate-500 font-medium max-w-[420px] mx-auto leading-relaxed">
                    {t("support.noResultsBody")}
                  </p>

                  <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
                    {popular.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setSearchQuery(tag)}
                        className="px-2.5 py-1 sm:px-3 rounded-full bg-white border border-slate-200 text-[10.5px] sm:text-xs font-semibold text-slate-600 hover:border-[#3D5EE1] hover:text-[#3D5EE1] transition-colors cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  <Link
                    href="/contact"
                    className="mt-5 sm:mt-7 inline-flex items-center gap-1.5 rounded-xl bg-[#3D5EE1] px-5 py-2.5 text-[11px] sm:text-sm font-bold text-white shadow-md hover:bg-[#2A48C9] transition-colors"
                  >
                    Ask the support team
                    <ArrowRight size={14} />
                  </Link>
                </div>
              )}
            </>
          )}
        </section>

        {/* ── FAQ Section ── */}
        <FAQSection
          heading={<RichText text={t("support.faqHeading")} />}
          categories={supportFaq}
        />

        {/* ── Support Banner (With Phone & Email) ── */}
        <section className="max-w-[1280px] mx-auto px-3 sm:px-6 pb-12 sm:pb-20">
          <div className="bg-gradient-to-r from-[#3D5EE1] to-[#172B88] rounded-2xl sm:rounded-3xl p-5 sm:p-12 text-white flex flex-col md:flex-row items-center justify-between gap-5 sm:gap-8 shadow-xl">
            <div className="text-center md:text-left">
              <h3 className="text-xl sm:text-3xl font-light mb-1 sm:mb-2">{t("support.stillNeedHelp")}</h3>
              <p className="text-brand-100 text-xs sm:text-base font-medium max-w-[500px]">
                {t("support.stillNeedHelpBody")}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 shrink-0 w-full md:w-auto">
              <a
                href={`tel:${phoneTel(contacts.supportPhone)}`}
                className="px-4 py-2.5 sm:px-6 sm:py-3.5 rounded-xl bg-white text-[#3D5EE1] font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors shadow-md flex-1 sm:flex-none"
              >
                <Phone size={14} className="sm:w-4 sm:h-4" />
                <span>{phoneDisplay(contacts.supportPhone)}</span>
              </a>
              <a
                href={`mailto:${contacts.supportEmail}`}
                className="px-4 py-2.5 sm:px-6 sm:py-3.5 rounded-xl bg-[#00A884] text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 hover:bg-[#008f70] transition-colors shadow-md border border-emerald-400/30 flex-1 sm:flex-none"
              >
                <Mail size={14} className="sm:w-4 sm:h-4" />
                <span>{contacts.supportEmail}</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <CTASection />
      <Footer />
    </div>
  );
}
